<template>
  <div v-if="loaded">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Edit: {{ form.abbreviation || `ID ${id}` }}</h4>
      <router-link :to="{ name: 'Accreditations' }" class="btn btn-default btn-sm">Back to list</router-link>
    </div>

    <card>
      <div class="row">
        <div class="col-md-4">
          <label>Abbreviation *</label>
          <el-input v-model="form.abbreviation" placeholder="e.g. JCI" class="mb-3" />

          <label>Country Code</label>
          <el-input v-model="form.country_code" placeholder="e.g. US" maxlength="2" class="mb-3" />

          <label>Website</label>
          <el-input v-model="form.web_site_link" placeholder="https://..." class="mb-3" />

          <label>Logo</label>
          <div class="accreditation-logo mb-3">
            <img v-if="logoPreview" :src="logoPreview" class="accreditation-logo__img" />
            <div>
              <button type="button" class="accreditation-logo__btn" @click.prevent="$refs.logoInput.click()">
                Upload Logo
              </button>
              <input type="file" ref="logoInput" accept="image/jpeg,image/png" @change="handleLogoUpload" style="display:none" />
            </div>
            <small v-if="logoFile" class="text-success d-block mt-1">{{ logoFile.name }}</small>
          </div>
        </div>
        <div class="col-md-8">
          <el-tabs v-model="activeLang">
            <el-tab-pane v-for="lang in langs" :key="lang" :label="lang.toUpperCase()" :name="lang">
              <label>Name {{ lang.toUpperCase() }}</label>
              <el-input v-model="form.name[lang]" :placeholder="`Name in ${lang.toUpperCase()}`" class="mb-3" />

              <label>Description {{ lang.toUpperCase() }}</label>
              <el-input type="textarea" :rows="6" v-model="form.description[lang]" :placeholder="`Description in ${lang.toUpperCase()}`" />
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <div class="mt-4">
        <el-button type="primary" :loading="saving" @click="saveItem">Save</el-button>
        <router-link :to="{ name: 'Accreditations' }">
          <el-button>Cancel</el-button>
        </router-link>
      </div>
    </card>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

function emptyLangs(langs = []) {
  return langs.reduce((acc, l) => { acc[l] = ""; return acc; }, {});
}

export default {
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      loaded: false,
      saving: false,
      activeLang: "en",
      logoFile: null,
      logoDataUrl: null,
      form: {
        id: 0,
        name: {},
        description: {},
        abbreviation: "",
        web_site_link: "",
        logo: "",
        country_code: "",
      },
    };
  },
  computed: {
    ...mapState("specialities", ["accreditations"]),
    ...mapState("cmsApiCatalogs", ["domains"]),
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    logoPreview() {
      return this.logoDataUrl || this.form.logo || "";
    },
  },
  methods: {
    ...mapActions("specialities", ["getAccreditations", "upsertAccreditation"]),
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    fillForm(row) {
      this.form = {
        id: row.id || 0,
        name: { ...emptyLangs(this.langs), ...(row.name || {}) },
        description: { ...emptyLangs(this.langs), ...(row.description || {}) },
        abbreviation: row.abbreviation || "",
        web_site_link: row.web_site_link || "",
        logo: row.logo || "",
        country_code: row.country_code || "",
      };
      this.logoFile = null;
      this.logoDataUrl = null;
      this.activeLang = this.langs[0] || "en";
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        return alert("Only .jpg, .jpeg, .png files are allowed");
      }
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        this.logoDataUrl = reader.result;
        this.logoFile = file;
      });
      reader.readAsDataURL(file);
    },
    async saveItem() {
      if (!this.form.abbreviation) {
        return this.$notify({ type: "warning", title: "Abbreviation is required" });
      }
      if (!this.form.name.en) {
        return this.$notify({ type: "warning", title: "Name EN is required" });
      }
      this.saving = true;
      try {
        await this.upsertAccreditation({
          data: {
            id: this.form.id,
            name: this.form.name,
            description: this.form.description,
            abbreviation: this.form.abbreviation,
            web_site_link: this.form.web_site_link,
            country_code: this.form.country_code,
            logo: this.form.logo,
          },
          logoFile: this.logoFile,
        });
        this.$notify({ type: "success", title: "Accreditation saved" });
        await this.getAccreditations();
        const updated = this.accreditations.find((a) => String(a.id) === String(this.id));
        if (updated) this.fillForm(updated);
      } catch (e) {
        this.$notify({ type: "warning", title: "Error saving accreditation" });
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    loadAccreditation() {
      const row = this.accreditations.find((a) => String(a.id) === String(this.id));
      if (!row) {
        this.$notify({ type: "warning", title: "Accreditation not found" });
        this.$router.replace({ name: "Accreditations" });
        return;
      }
      this.fillForm(row);
      this.loaded = true;
    },
  },
  async created() {
    await Promise.all([
      this.getDomains(),
      this.getAccreditations(),
    ]);
    this.loadAccreditation();
  },
};
</script>

<style scoped>
.accreditation-logo__img {
  display: block;
  max-width: 150px;
  max-height: 80px;
  margin-bottom: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 4px;
  background: #fff;
  object-fit: contain;
}
.accreditation-logo__btn {
  padding: 6px 16px;
  background: #f4f8fb;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.accreditation-logo__btn:hover {
  background: #e9ecef;
}
</style>
